import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { catchError, delay } from 'rxjs/operators';
import { ExceptionService } from './util/exception-request.service';
import {
  IContentArticles,
  IContentCarousel,
  IContentHome,
  IContentPlans,
} from '../interfaces';
import { AuthService } from './auth.service';
import { UtilsService } from './util/utils.service';

@Injectable({
  providedIn: 'root',
})
export class CmsSeccionesService {
  private readonly homePath: string = `${environment.base_url}/crud-title/main-titles`;
  private readonly carouselPath: string = `${environment.base_url}/crud-carousel/carousels`;
  private readonly articlesPath: string = `${environment.base_url}/crud-article/articles`;
  private readonly plansPath: string = `${environment.base_url}/crud-memberships/membresias`;
  private readonly filesDataPath: string = `${environment.base_url}/update-infracciones`;
  private readonly entityCategoriesPath: string = `${environment.base_url}/categorias`;
  private readonly entityOthersCategoriesPath: string = `${environment.base_url}/update-infracciones/tipos-infracciones`;
  private readonly entitySubCategoriesPath: string = `${environment.base_url}/subcategorias`;
  private readonly unstructuredFilesPath: string = `${environment.base_url}/pdf-processor`;
  private readonly saveUnstructuredFilesPath: string = `${environment.base_url}/pdf-processor/save`;
  private readonly structuredReceiverPath = `${environment.base_url}/scraper-receiver`;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly exceptionService: ExceptionService,
    private readonly authService: AuthService,
    private readonly utilsService: UtilsService
  ) {}

  public getContentHome(): Observable<any> {
    return this.httpClient
      .get(this.homePath)
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public updateContentHome(contentHome: IContentHome): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    const { titulo } = contentHome;
    return this.httpClient
      .put(`${this.homePath}/${contentHome.id}`, { titulo }, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public getContentCarousel(queryParams?: any): Observable<any> {
    const params = this.utilsService.generateParams(queryParams);
    return this.httpClient
      .get(this.carouselPath, { params: params })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public createContentCarousel(slideCarousel: FormData): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient
      .post(this.carouselPath, slideCarousel, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public updateContentCarousel(
    contentCarousel: FormData | IContentCarousel
  ): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    let contentCarouselId: number | undefined = 0;
    if (contentCarousel instanceof FormData) {
      contentCarouselId = +(contentCarousel.get('id') as string);
      contentCarousel.delete('id');
    } else {
      contentCarouselId = contentCarousel.id;
      delete contentCarousel.id;
    }
    return this.httpClient
      .put(`${this.carouselPath}/${contentCarouselId}`, contentCarousel, {
        headers,
      })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public deleteContentCarousel(slideCarouselId: number): Observable<void> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient.delete<void>(
      `${this.carouselPath}/${slideCarouselId}`,
      { headers }
    );
  }

  public getContentArticles(queryParams?: any): Observable<any> {
    const params = this.utilsService.generateParams(queryParams);
    return this.httpClient
      .get(this.articlesPath, { params: params })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public createContentArticle(article: IContentArticles): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient
      .post(this.articlesPath, article, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public updateContentArticle(article: IContentArticles): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient
      .put(`${this.articlesPath}/${article.id}`, article, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public deleteContentArticle(articleId: number): Observable<void> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient
      .delete<void>(`${this.articlesPath}/${articleId}`, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public getContentPlans(queryParams?: any): Observable<any> {
    const params = this.utilsService.generateParams(queryParams);
    return this.httpClient
      .get(this.plansPath, { params: params })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public createContentPlan(plan: FormData): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient
      .post(this.plansPath, plan, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public updateContentPlan(plan: FormData | IContentPlans): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    let planId: number | undefined = 0;
    if (plan instanceof FormData) {
      planId = +(plan.get('id') as string);
      plan.delete('id');
    } else {
      planId = plan.id;
      delete plan.id;
    }
    return this.httpClient
      .put(`${this.plansPath}/${planId}`, plan, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public deleteContentPlans(planId: number): Observable<void> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient
      .delete<void>(`${this.plansPath}/${planId}`, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  //  CRUD Administración de categorías (Infracciones, Contratos, etc)
  //  y subcategorías (Infracciones ambientales, Infracciones Laboreales)

  public getOthersCategories(): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient
      .get(`${this.entityOthersCategoriesPath}`, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public getCategories(): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient
      .get(`${this.entityCategoriesPath}`, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public createCategory(newCategory: any): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient
      .post(`${this.entityCategoriesPath}`, newCategory, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public updateCategory(category: any): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient
      .put(
        `${this.entityCategoriesPath}/${category.id}`,
        { title: category.title },
        { headers }
      )
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public deleteCategory(categoryId: number): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient
      .delete(`${this.entityCategoriesPath}/${categoryId}`, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public getSubCategories(categoryId: number): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient
      .get(`${this.entityCategoriesPath}/${categoryId}/subcategorias`, {
        headers,
      })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public createSubCategory(newSubCategory: any): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient
      .post(`${this.entitySubCategoriesPath}`, newSubCategory, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public updateSubCategory(subCategory: any): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient
      .put(
        `${this.entitySubCategoriesPath}/${subCategory.id}`,
        { title: subCategory.title },
        { headers }
      )
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public deleteSubCategory(subCategoryId: number): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient
      .delete(`${this.entitySubCategoriesPath}/${subCategoryId}`, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public updateSubCategoryPrompt(
    subCategoryId: number,
    prompt: string
  ): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });

    return this.httpClient
      .put(
        `${this.entitySubCategoriesPath}/${subCategoryId}`,
        { prompt },
        { headers }
      )
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  // Archivos estructurados
  public uploadStructuredFile(
    formData: FormData,
    queryParams?: any
  ): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    const params = this.utilsService.generateParams(queryParams);
    return this.httpClient
      .post(`${this.filesDataPath}/upload`, formData, {
        headers: headers,
        params: params,
      })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public getUploadStructuredFiles(queryParams?: any): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    const params = this.utilsService.generateParams(queryParams);
    return this.httpClient
      .get(`${this.filesDataPath}/list`, { headers: headers, params: params })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public getPreviewDataStructuredFile(jobId: string): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient
      .get(`${this.filesDataPath}/job-data/${jobId}`, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public deleteStructuredFile(jobId: string): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient
      .delete(`${this.filesDataPath}/job/${jobId}`, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public saveVerifiedStructuredData(jobId: string,tipoInfraccion: string,data: any[]): Observable<any> {
  const headers = new HttpHeaders({
    Authorization: `Bearer ${this.authService.token}`,
  });

  const body = {
    jobId: jobId,
    tipo_infraccion: tipoInfraccion,
    data: data,
  };

  return this.httpClient
    .post(`${this.structuredReceiverPath}/process-verified`, body, { headers })
    .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
}

  public getStructuredFileStatus(jobId: string): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });

    return this.httpClient
      .get(`${this.structuredReceiverPath}/status/${jobId}`, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  // Archivos No estructurados
  public uploadUnstructuredFile(formData: FormData): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });

    return this.httpClient
      .post(`${this.unstructuredFilesPath}/process`, formData, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public getUnstructuredFiles(queryParams?: any): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    const params = this.utilsService.generateParams(queryParams);
    return this.httpClient
      .get(`${this.unstructuredFilesPath}/list`, {
        headers: headers,
        params: params,
      })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public getUnstructuredFileStatus(jobId: string): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient
      .get(`${this.unstructuredFilesPath}/status/${jobId}`, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public getPreviewDataUnstructuredFile(jobId: string): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient
      .get(`${this.filesDataPath}/job-data/${jobId}`, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public deleteUnstructuredFile(jobId: string): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });
    return this.httpClient
      .delete(`${this.unstructuredFilesPath}/delete/${jobId}`, { headers })
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  public saveProcessedData(jobId: string, datos: any): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.token}`,
    });

    return this.httpClient
      .post(
        `${this.saveUnstructuredFilesPath}/${jobId}`,
        { datos },
        { headers }
      )
      .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
  }

  //BUSQUEDA-MASIVA

  public getAllSubCategories(): Observable<any> {
  const headers = new HttpHeaders({
    Authorization: `Bearer ${this.authService.token}`,
  });
  return this.httpClient
    .get(`${this.entitySubCategoriesPath}`, { headers })
    .pipe(catchError((ex) => this.exceptionService.catchBadResponse(ex)));
}

}
